import ExcelJS from 'exceljs';
import download from 'downloadjs';
import sanitize from 'sanitize-filename';
import { parseISO } from 'date-fns';

import { Project } from '~/types/project';
import { Portfolio, PortfolioTotals } from '~/types/portfolio';
import projectFormConditions from '~/schema/project/conditions';
import projectFormConfig from '~/schema/project/config';
import formConditions from '~/utils/formConditions';
import { ProjectWorksheetConfig } from '~/features/projectWizard/worksheets/project/constants';
import { colors } from '~/theme';
import { sizeKwdc } from '~/utils/calculators';
import { state2abbreviation } from '~/utils/states';
import {
  CellValue,
  ManifestEntry,
  ManifestFormat,
  additionalExpensePerYear,
  additionalRevenuePerYear,
  dealSpreadsheetManifest,
  dynamicSections,
  orderedAdderLabelsFor,
} from './dealSpreadsheetManifest';

export interface ProjectBlob {
  project: Project;
  projectCosts?: any;
  projectDisplayValues?: any;
}

interface RowSpec {
  /** Stable manifest row id, when this row originates from a manifest entry. */
  id?: string;
  label: string;
  format?: ManifestFormat;
  perProject: ResolvedCell[];
  total: ResolvedCell;
}

interface SectionAcc {
  sectionId: string;
  sectionLabel: string;
  subsectionGroups: SubsectionAcc[];
}

interface SubsectionAcc {
  subsectionId: string;
  subsectionLabel: string;
  rows: RowSpec[];
}

const sanitizeFilename = (s: string) => s.replaceAll(/[*?\\/:[\]]/g, '_');

const removeForbiddenExcelCharacters = (str: string) => str.replaceAll(/(\*|\?|\\|\/|:|\[|\])/g, '_');

const buildWorksheetName = (name: string, sizeKwdc: number) => {
  const fixed = ` - ${Math.round(sizeKwdc)}kW`;
  return removeForbiddenExcelCharacters(name.substring(0, 31 - fixed.length) + fixed);
};

/** Budget total rows are rendered as a live SUM of their component rows so the
 * sheet recalculates when a user edits the inputs above. */
const BUDGET_TOTAL_COMPONENTS: Record<string, string[]> = {
  budgetTotalPerWdc: ['epcCostPerWdc', 'budgetInterconnectionPerWdc', 'budgetOtherItcPerWdc', 'budgetOtherNonItcPerWdc', 'conductorFeePerWdc'],
  budgetTotal: ['epcCost', 'budgetInterconnection', 'budgetOtherItc', 'budgetOtherNonItc', 'conductorFee'],
};

const numFormatFor = (format?: ManifestFormat): string | undefined => {
  switch (format) {
    case 'currency': return '$#,##0';
    case 'currencyPerW': return '$0.000';
    case 'integer': return '#,##0';
    case 'decimal2': return '0.00';
    case 'decimal4': return '0.0000';
    case 'percent': return '0.00%';
    case 'percentInteger': return '0%';
    case 'percentRaw': return '0.00%';
    case 'date': return 'm/d/yyyy';
    default: return undefined;
  }
};

type ResolvedCell = CellValue | Date;

const coerceCell = (value: CellValue, format?: ManifestFormat): ResolvedCell => {
  if (value === undefined || value === null || value === '') return '';
  if (format === 'date' && typeof value === 'string') {
    try { return parseISO(value); } catch { return value; }
  }
  if ((format === 'percent' || format === 'percentInteger') && typeof value === 'number') return value / 100;
  if (format === 'yesNo' && typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value;
};

const isNumericFormat = (format?: ManifestFormat): boolean =>
  format === 'currency' || format === 'currencyPerW' || format === 'integer'
  || format === 'decimal2' || format === 'decimal4'
  || format === 'percent' || format === 'percentInteger' || format === 'percentRaw';

const buildEvaluators = (blobs: ProjectBlob[]) => blobs.map(({ project }) => formConditions({
  data: project,
  config: projectFormConfig,
}));

const defaultSectionLabel = (sectionId: string): string =>
  ProjectWorksheetConfig.find(s => s.id === sectionId)?.label ?? sectionId;

const defaultSubsectionLabel = (sectionId: string, subsectionId: string): string =>
  ProjectWorksheetConfig.find(s => s.id === sectionId)
    ?.subsections.find(ss => ss.id === subsectionId)?.label ?? subsectionId;

/** Return the value if every entry in the array is the same non-empty string, else undefined. */
const unanimous = (values: Array<string | undefined>): string | undefined => {
  const defined = values.filter((v): v is string => typeof v === 'string' && v.length > 0);
  if (defined.length === 0 || defined.length !== values.length) return undefined;
  return defined.every(v => v === defined[0]) ? defined[0] : undefined;
};

const sectionLabel = (
  sectionId: string,
  blobs: ProjectBlob[],
  evaluators: ReturnType<typeof buildEvaluators>,
): string => {
  const def = defaultSectionLabel(sectionId);
  const overrides = blobs
    .map((_, i) => evaluators[i])
    .filter(ev => ev.isSectionDisplaying(sectionId))
    .map(ev => ev.sectionLabel(sectionId, def));
  return unanimous(overrides) ?? def;
};

const subsectionLabel = (
  sectionId: string,
  subsectionId: string,
  blobs: ProjectBlob[],
  evaluators: ReturnType<typeof buildEvaluators>,
): string => {
  const def = defaultSubsectionLabel(sectionId, subsectionId);
  const overrides = blobs
    .map((_, i) => evaluators[i])
    .filter(ev => ev.isSectionDisplaying(sectionId) && ev.isSubsectionDisplaying(subsectionId))
    .map(ev => ev.subsectionLabel(subsectionId, def));
  return unanimous(overrides) ?? def;
};

const evalDisplay = (
  evaluators: ReturnType<typeof buildEvaluators>,
  blobs: ProjectBlob[],
  entry: ManifestEntry,
  projectIdx: number,
): boolean => {
  const ev = evaluators[projectIdx];
  if (!ev.isSectionDisplaying(entry.sectionId)) return false;
  if (!ev.isSubsectionDisplaying(entry.subsectionId)) return false;
  const fieldId = entry.fieldId ?? entry.gateFieldId;
  if (fieldId && ev.isFieldDisplaying(fieldId)) return true;
  // A bid-placeholder row is force-shown when its pricing type matches, even
  // though the underlying worksheet field is hidden for that bid type (the
  // value is the thing being bid, e.g. "TO BID"). Section/subsection gating
  // still applies (so e.g. an operating-acquisition budget stays hidden).
  if (entry.bidPlaceholder?.(blobs[projectIdx].project)) return true;
  return fieldId ? false : true;
};

const buildRowForEntry = (
  entry: ManifestEntry,
  blobs: ProjectBlob[],
  evaluators: ReturnType<typeof buildEvaluators>,
  includeTotals: boolean,
): RowSpec | null => {
  let anyDisplaying = false;
  const perProject: ResolvedCell[] = blobs.map((blob, i) => {
    if (!evalDisplay(evaluators, blobs, entry, i)) return '';
    anyDisplaying = true;
    const raw = entry.extract(blob.project, {
      projectCosts: blob.projectCosts,
      projectDisplayValues: blob.projectDisplayValues,
    });
    // A bid row with no value yet renders the literal "TO BID" (red) instead of a blank.
    if ((raw === undefined || raw === null || raw === '') && entry.bidPlaceholder?.(blob.project)) {
      return 'TO BID';
    }
    return coerceCell(raw, entry.format);
  });
  if (!anyDisplaying) return null;
  if (entry.hideWhenAllEmpty && perProject.every(v => v === '' || v === null || v === undefined)) return null;

  const visibleIndices = blobs
    .map((_, i) => i)
    .filter(i => evalDisplay(evaluators, blobs, entry, i));
  const visibleProjects = visibleIndices.map(i => blobs[i].project);
  const format = entry.formatFor ? (entry.formatFor(visibleProjects) ?? entry.format) : entry.format;

  let total: ResolvedCell = '';
  if (entry.totalEligible && isNumericFormat(format)) {
    let sum = 0;
    let hasAny = false;
    for (const v of perProject) {
      if (typeof v === 'number' && !Number.isNaN(v)) {
        sum += v;
        hasAny = true;
      }
    }
    if (hasAny) total = sum;
  } else if (includeTotals && visibleIndices.length >= 2 && !entry.totalEligible) {
    // Dedupe rule: when every visible project has the same non-empty value
    // for this row, surface it once in the Portfolio column and blank the
    // per-project cells. Skipped for numeric totalEligible rows (they sum).
    const visibleValues = visibleIndices.map(i => perProject[i]);
    const allDefined = visibleValues.every(v => v !== '' && v !== null && v !== undefined);
    if (allDefined) {
      const first = visibleValues[0];
      const allEqual = visibleValues.every(v => {
        if (v instanceof Date && first instanceof Date) return v.getTime() === first.getTime();
        return v === first;
      });
      if (allEqual) {
        total = first;
        for (const i of visibleIndices) perProject[i] = '';
      }
    }
  }

  let label: string;
  const overrideLabel = entry.labelFor?.(visibleProjects);
  if (overrideLabel != null) {
    label = overrideLabel;
  } else if (entry.fieldId) {
    const wsLabel = unanimous(visibleIndices.map(i => evaluators[i].getFieldProps(entry.fieldId!)?.fieldLabel));
    label = wsLabel ? `${wsLabel}${entry.unitSuffix ?? ''}` : entry.label;
  } else {
    label = entry.label;
  }
  return { id: entry.id, label, format, perProject, total };
};

const buildDynamicRows = (
  blobs: ProjectBlob[],
  evaluators: ReturnType<typeof buildEvaluators>,
  spec: { sectionId: string; subsectionId: string; fieldId: string; headerLabel: string },
  itemsFor: (project: Project) => Array<{ name?: string; amount?: number; unit?: string; escalator?: number; expectedTerm?: number; notes?: string }>,
  perYear: (item: any, project: Project) => number | undefined,
  options: { withNotes?: boolean } = {},
): RowSpec[] => {
  const visible = blobs.map((_, i) => {
    const ev = evaluators[i];
    return ev.isSectionDisplaying(spec.sectionId)
      && ev.isSubsectionDisplaying(spec.subsectionId)
      && ev.isFieldDisplaying(spec.fieldId);
  });
  if (!visible.some(Boolean)) return [];

  const labels: string[] = [];
  const seen = new Set<string>();
  blobs.forEach((blob, i) => {
    if (!visible[i]) return;
    (itemsFor(blob.project) || []).forEach((item, idx) => {
      const label = (item.name && item.name.trim()) || `${spec.headerLabel} ${idx + 1}`;
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    });
  });

  const findItem = (blob: ProjectBlob, label: string, blobVisible: boolean) => {
    if (!blobVisible) return undefined;
    const items = itemsFor(blob.project) || [];
    return items.find((it, idx) => ((it.name && it.name.trim()) || `${spec.headerLabel} ${idx + 1}`) === label);
  };

  const rows: RowSpec[] = [];
  for (const label of labels) {
    let sum = 0;
    let hasAny = false;
    const perProject: ResolvedCell[] = blobs.map((blob, i) => {
      const item = findItem(blob, label, visible[i]);
      if (!item) return '';
      const value = perYear(item, blob.project);
      if (typeof value === 'number' && !Number.isNaN(value)) {
        sum += value;
        hasAny = true;
      }
      return coerceCell(value, 'currency');
    });
    rows.push({ label: `${label} ($/year)`, format: 'currency', perProject, total: hasAny ? sum : '' });

    if (options.withNotes) {
      const notesPerProject: ResolvedCell[] = blobs.map((blob, i) => {
        const item = findItem(blob, label, visible[i]);
        return coerceCell(item?.notes, 'text');
      });
      rows.push({ label: `${label} Notes`, format: 'text', perProject: notesPerProject, total: '' });
    }
  }
  return rows;
};

const collectSections = (
  blobs: ProjectBlob[],
  evaluators: ReturnType<typeof buildEvaluators>,
  includeTotals: boolean,
): SectionAcc[] => {
  const accBySection = new Map<string, SectionAcc>();
  const subsectionByKey = new Map<string, SubsectionAcc>();

  for (const entry of dealSpreadsheetManifest) {
    const row = buildRowForEntry(entry, blobs, evaluators, includeTotals);
    if (!row) continue;

    let section = accBySection.get(entry.sectionId);
    if (!section) {
      section = { sectionId: entry.sectionId, sectionLabel: sectionLabel(entry.sectionId, blobs, evaluators), subsectionGroups: [] };
      accBySection.set(entry.sectionId, section);
    }

    const key = `${entry.sectionId}::${entry.subsectionId}`;
    let subsection = subsectionByKey.get(key);
    if (!subsection) {
      subsection = { subsectionId: entry.subsectionId, subsectionLabel: subsectionLabel(entry.sectionId, entry.subsectionId, blobs, evaluators), rows: [] };
      subsectionByKey.set(key, subsection);
      section.subsectionGroups.push(subsection);
    }
    subsection.rows.push(row);
  }

  // Dynamic expanders: additional revenues + additional operating expenses
  const additionalRevRows = buildDynamicRows(
    blobs, evaluators, dynamicSections.additionalRevenue,
    p => p.additionalRevenues ?? [],
    additionalRevenuePerYear,
  );
  if (additionalRevRows.length) {
    let section = accBySection.get(dynamicSections.additionalRevenue.sectionId);
    if (!section) {
      section = { sectionId: dynamicSections.additionalRevenue.sectionId, sectionLabel: sectionLabel(dynamicSections.additionalRevenue.sectionId, blobs, evaluators), subsectionGroups: [] };
      accBySection.set(section.sectionId, section);
    }
    const subsection: SubsectionAcc = {
      subsectionId: dynamicSections.additionalRevenue.subsectionId,
      subsectionLabel: subsectionLabel(dynamicSections.additionalRevenue.sectionId, dynamicSections.additionalRevenue.subsectionId, blobs, evaluators),
      rows: additionalRevRows,
    };
    section.subsectionGroups.push(subsection);
  }

  const additionalExpRows = buildDynamicRows(
    blobs, evaluators, dynamicSections.additionalOperatingExpenses,
    p => p.additionalOperatingExpenses ?? [],
    additionalExpensePerYear,
    { withNotes: true },
  );
  if (additionalExpRows.length) {
    let section = accBySection.get(dynamicSections.additionalOperatingExpenses.sectionId);
    if (!section) {
      section = { sectionId: dynamicSections.additionalOperatingExpenses.sectionId, sectionLabel: sectionLabel(dynamicSections.additionalOperatingExpenses.sectionId, blobs, evaluators), subsectionGroups: [] };
      accBySection.set(section.sectionId, section);
    }
    const subsection: SubsectionAcc = {
      subsectionId: dynamicSections.additionalOperatingExpenses.subsectionId,
      subsectionLabel: subsectionLabel(dynamicSections.additionalOperatingExpenses.sectionId, dynamicSections.additionalOperatingExpenses.subsectionId, blobs, evaluators),
      rows: additionalExpRows,
    };
    section.subsectionGroups.push(subsection);
  }

  // ITC Adder N rows: one row per slot, where slot N holds the Nth selected
  // adder (in canonical order) for each project. Rows live inside the Budget /
  // Upfront incentives subsection, right after "Expected Total ITC %".
  const upfrontSubsection = accBySection.get('budget')?.subsectionGroups.find(ss => ss.subsectionId === 'upfrontIncentives');
  if (upfrontSubsection) {
    const visibleAdders = blobs.map((blob, i) =>
      evaluators[i].isFieldDisplaying('itcAdders') ? orderedAdderLabelsFor(blob.project) : []);
    const maxAdders = Math.max(0, ...visibleAdders.map(a => a.length));
    const adderRows: RowSpec[] = [];
    for (let n = 0; n < maxAdders; n++) {
      adderRows.push({
        label: `ITC Adder ${n + 1}`,
        format: 'text',
        perProject: visibleAdders.map(a => a[n] ?? ''),
        total: '',
      });
    }
    if (adderRows.length) {
      const idx = upfrontSubsection.rows.findIndex(r => r.label === 'Expected Total ITC %');
      const insertAt = idx >= 0 ? idx + 1 : 0;
      upfrontSubsection.rows.splice(insertAt, 0, ...adderRows);
    }
  }

  // Storage subsection: when no visible project has storage, replace the
  // subsection (or insert one) with a single placeholder row.
  const systemSection = accBySection.get('system');
  if (systemSection) {
    const hasStorageProject = blobs.some((blob, i) => evaluators[i].isFieldDisplaying('storage') && blob.project.hasStorage);
    if (!hasStorageProject) {
      const placeholderRow: RowSpec = {
        label: 'N/A - no storage in the portfolio',
        format: 'text',
        perProject: blobs.map(() => ''),
        total: '',
      };
      const existing = systemSection.subsectionGroups.find(ss => ss.subsectionId === 'storage');
      if (existing) {
        existing.rows = [placeholderRow];
      } else {
        systemSection.subsectionGroups.push({
          subsectionId: 'storage',
          subsectionLabel: subsectionLabel('system', 'storage', blobs, evaluators),
          rows: [placeholderRow],
        });
      }
    }
  }

  // Preserve worksheet section order
  const order = ProjectWorksheetConfig.map(s => s.id);
  return order
    .map(id => accBySection.get(id))
    .filter((s): s is SectionAcc => Boolean(s));
};

interface BuildWorkbookOptions {
  /** Label shown in the top-left corner above the row labels (e.g. portfolio name or project name). */
  topLeftLabel: string;
  /** Worksheet tab name. */
  sheetName: string;
  /** When true, append a Totals column summing `totalEligible` numeric rows. */
  includeTotals: boolean;
  /** Optional ISO bid deadline displayed in the top callout area. */
  bidDeadline?: string;
}

const bidRequestedLabel = (blobs: ProjectBlob[]): string | undefined => {
  const labels: string[] = [];
  for (const b of blobs) {
    const p = b.project;
    let l: string | undefined;
    switch (p.pricingType) {
      case 'ACQUISITION': l = p.developmentStage === 'OPERATING' ? 'Acquisition Price' : 'EPC Cost + Dev Fee'; break;
      case 'DEV_FEE': l = 'Development Fee'; break;
      case 'OFFTAKE': l = 'Offtake Rate'; break;
      case 'ITC': l = 'ITC Transfer'; break;
      default: break;
    }
    if (l) labels.push(l);
  }
  if (labels.length === 0) return undefined;
  const unique = Array.from(new Set(labels));
  return unique.length === 1 ? unique[0] : unique.join(', ');
};

const formatBidDeadline = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
};

/** Build the projects-as-columns deal spreadsheet workbook. Used for both portfolio and single-project flows. */
export const buildDealSpreadsheetWorkbook = (
  blobs: ProjectBlob[],
  options: BuildWorkbookOptions,
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const evaluators = buildEvaluators(blobs);
  const sections = collectSections(blobs, evaluators, options.includeTotals);

  const ws = workbook.addWorksheet(options.sheetName, {
    views: [{ showGridLines: false, state: 'frozen', xSplit: 1 }],
  });
  ws.pageSetup.showGridLines = false;

  const labelColWidth = 38;
  const valueColWidth = 22;
  const leadingCols = options.includeTotals ? 1 : 0;
  const lastCol = blobs.length + 1 + leadingCols;
  // Portfolio column (when present) sits immediately after the label column,
  // before per-project columns.
  ws.columns = [
    { width: labelColWidth },
    ...(options.includeTotals ? [{ width: valueColWidth }] : []),
    ...blobs.map(() => ({ width: valueColWidth })),
  ];
  const portfolioCol = options.includeTotals ? 2 : 0;

  const headerFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: colors.midnightBlue.substring(1) },
  };
  const sectionHeaderFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: colors.blue.substring(1) },
  };
  const subHeaderFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE6EAF1' },
  };

  const sellerName = blobs[0]?.project.installer?.company?.name;
  if (sellerName) {
    const bioRow = ws.addRow([`Seller: ${sellerName}`]);
    bioRow.height = 26;
    const nameCell = bioRow.getCell(1);
    nameCell.font = { bold: true, size: 14, color: { argb: colors.midnightBlue.substring(1) } };
    nameCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

    for (let c = 1; c <= lastCol; c++) {
      bioRow.getCell(c).border = { bottom: { style: 'thin', color: { argb: 'FFD0D7E0' } } };
    }
  }

  const calloutPairs: Array<[string, string]> = [];
  const deadlineText = formatBidDeadline(options.bidDeadline);
  if (deadlineText) calloutPairs.push(['Bid Due:', deadlineText]);
  const bidLabel = bidRequestedLabel(blobs);
  if (bidLabel) calloutPairs.push(['Bid Requested:', bidLabel]);
  for (const [label, value] of calloutPairs) {
    const calloutRow = ws.addRow([`${label} ${value}`]);
    calloutRow.height = 18;
    const cell = calloutRow.getCell(1);
    cell.font = { bold: true, size: 11, color: { argb: colors.midnightBlue.substring(1) } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    for (let c = 1; c <= lastCol; c++) {
      calloutRow.getCell(c).border = { bottom: { style: 'thin', color: { argb: 'FFD0D7E0' } } };
    }
  }

  const headerCells = [
    options.topLeftLabel,
    ...(options.includeTotals ? ['Portfolio'] : []),
    ...blobs.map(b => b.project.name),
  ];
  const headerRow = ws.addRow(headerCells);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
  headerRow.height = 24;
  headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  headerRow.eachCell(cell => { cell.fill = headerFill; });
  headerRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

  // Track the Excel row number of each manifest row so budget totals can be
  // written as live SUM formulas referencing their component rows.
  const rowNumberById = new Map<string, number>();

  for (const section of sections) {
    if (section.subsectionGroups.length === 0) continue;

    const sectionHeader = ws.addRow([section.sectionLabel]);
    sectionHeader.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    sectionHeader.height = 20;
    for (let c = 1; c <= lastCol; c++) {
      const cell = sectionHeader.getCell(c);
      cell.fill = sectionHeaderFill;
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    }

    for (const subsection of section.subsectionGroups) {
      if (subsection.subsectionLabel && subsection.subsectionLabel !== section.sectionLabel) {
        const subHeader = ws.addRow([subsection.subsectionLabel]);
        subHeader.font = { bold: true, italic: true };
        for (let c = 1; c <= lastCol; c++) {
          subHeader.getCell(c).fill = subHeaderFill;
        }
      }

      for (const row of subsection.rows) {
        const format = row.format;
        const numFmt = numFormatFor(format);
        const excelRow = ws.addRow([
          row.label,
          ...(options.includeTotals ? [row.total] : []),
          ...row.perProject,
        ]);
        if (row.id) rowNumberById.set(row.id, excelRow.number);
        excelRow.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        for (let c = 2; c <= lastCol; c++) {
          const cell = excelRow.getCell(c);
          cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
          if (numFmt && typeof cell.value === 'number') cell.numFmt = numFmt;
          if (format === 'date' && cell.value instanceof Date) cell.numFmt = numFmt!;
          // "TO BID" placeholders render in red to draw attention.
          if (cell.value === 'TO BID') cell.font = { color: { argb: 'FFFF0000' } };
        }
        if (portfolioCol) {
          excelRow.getCell(portfolioCol).font = { bold: true };
        }

        // Budget totals: replace the static numeric cells with a live SUM of the
        // component rows above, and emphasize the row (bold + top border).
        const componentIds = row.id ? BUDGET_TOTAL_COMPONENTS[row.id] : undefined;
        if (componentIds) {
          const componentRows = componentIds
            .map(id => rowNumberById.get(id))
            .filter((n): n is number => typeof n === 'number');
          for (let c = 2; c <= lastCol; c++) {
            const cell = excelRow.getCell(c);
            if (typeof cell.value === 'number' && componentRows.length) {
              const addresses = componentRows.map(rn => ws.getCell(rn, c).address);
              cell.value = { formula: `SUM(${addresses.join(',')})`, result: cell.value } as any;
              if (numFmt) cell.numFmt = numFmt;
            }
          }
          for (let c = 1; c <= lastCol; c++) {
            const cell = excelRow.getCell(c);
            cell.font = { ...(cell.font ?? {}), bold: true };
            cell.border = { ...(cell.border ?? {}), top: { style: 'thin' } };
          }
          // Spacer row beneath each totals row so it reads as a distinct block.
          ws.addRow([]);
        }
      }
    }
  }

  const creditRow = ws.addRow([{ text: 'Spreadsheet generated by Conductor Solar', hyperlink: 'https://conductor.solar' }]);
  creditRow.height = 16;
  const creditCell = creditRow.getCell(1);
  creditCell.font = { italic: true, size: 9, color: { argb: colors.charcoal.substring(1) }, underline: true };
  creditCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  for (let c = 1; c <= lastCol; c++) {
    creditRow.getCell(c).fill = subHeaderFill;
  }

  return workbook;
};

const triggerDownload = async (workbook: ExcelJS.Workbook, baseFilename: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  download(
    new Blob([buffer]),
    sanitize(sanitizeFilename(baseFilename)),
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
};

/** Generate, name, and trigger download of the portfolio deal spreadsheet. */
export const generateDealSpreadsheet = async (
  portfolio: Portfolio,
  portfolioTotals: PortfolioTotals,
  blobs: ProjectBlob[],
): Promise<void> => {
  // Touch projectFormConditions so the import isn't tree-shaken (config already references it).
  void projectFormConditions;

  const workbook = buildDealSpreadsheetWorkbook(blobs, {
    topLeftLabel: portfolio.name,
    sheetName: buildWorksheetName(portfolio.name, portfolioTotals.sizeKwdc),
    includeTotals: true,
    bidDeadline: portfolio.bidDeadline,
  });
  const dateStr = new Date().toISOString().split('T')[0];
  const sizeMW = (portfolioTotals.sizeKwdc / 1000).toFixed(3);
  await triggerDownload(workbook, `${portfolio.name} - Entire portfolio - ${sizeMW}MW - ${dateStr}.xlsx`);
};

/** Generate, name, and trigger download of a single-project deal spreadsheet. Same layout as the portfolio version, with one project column and no Totals. */
export const generateProjectSpreadsheet = async (
  project: Project,
  projectCosts?: any,
  projectDisplayValues?: any,
): Promise<void> => {
  void projectFormConditions;

  const blob: ProjectBlob = { project, projectCosts, projectDisplayValues };
  const projectSizeKwdc = sizeKwdc(project.projectArrays ?? []);
  const workbook = buildDealSpreadsheetWorkbook([blob], {
    topLeftLabel: project.name,
    sheetName: buildWorksheetName(project.name, projectSizeKwdc),
    includeTotals: false,
    bidDeadline: project.bidDeadline ?? project.portfolio?.bidDeadline,
  });

  const dateStr = new Date().toISOString().split('T')[0];
  const sizeMW = (projectSizeKwdc / 1000).toFixed(3);
  const stateAbbrev = state2abbreviation(project.addressState);
  const portfolioPrefix = project.portfolio ? `${project.portfolio.name} - ` : '';
  const baseFilename = `${portfolioPrefix}${project.name} - ${stateAbbrev} - ${sizeMW}MW - ${dateStr}.xlsx`;
  await triggerDownload(workbook, baseFilename);
};
